import React, { useCallback, useMemo } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FaShoppingCart } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { InlineLoadingIcon } from "../../components/InlineLoadingIcon/InlineLoadingIcon";
import config from "../../config/config.json";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { useCart } from "../../hooks/use-cart";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { useSearchableInvestorSearchResults } from "../../hooks/use-searchable-investor-search-results";
import InvestorCard from "./InvestorCard";
import "./InvestorSearchPage.scss";

const InvestorSearchPage: React.FC = () => {
  const { isUserAdmin } = useAppAuthState();
  const { addSearchToCart } = useCart();

  useDocumentTitle("Investor Search");
  const [searchParams] = useSearchParams();

  // Extract search parameters from URL
  const stageFocus = searchParams.get("stageFocus");
  const sectorFocus = searchParams.get("sectorFocus");

  // Create search parameters object for the hook
  const searchParamsObj = useMemo(
    () => ({
      stageFocus: stageFocus || undefined,
      sectorFocus: sectorFocus || undefined,
    }),
    [stageFocus, sectorFocus],
  );

  // Get search results
  const { results, loading, error, hasResults, resultCount } =
    useSearchableInvestorSearchResults(searchParamsObj, {
      sort: "alphabetical",
    });

  // Create search bar value based on parameters
  const searchBarValue = useMemo(() => {
    if (stageFocus) {
      return `stageFocus:${stageFocus}`;
    }

    if (sectorFocus) {
      return `sectorFocus:${sectorFocus}`;
    }

    return "";
  }, [stageFocus, sectorFocus]);

  const handleAddSearchToCart = useCallback(() => {
    addSearchToCart(searchParamsObj);
  }, [addSearchToCart, searchParamsObj]);

  return (
    <div className="investor-search-page">
      <div className="page-header">
        Investor Search
        <div className="page-subheader">
          Search for investors by stage focus or sector focus
        </div>
      </div>

      <div className="search-section">
        <Form.Group>
          <Form.Label>Search</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter stageFocus:value or sectorFocus:value"
            value={searchBarValue}
            disabled={true}
            className="search-input"
          />
          <Form.Text className="text-muted">
            We'll add more search options soon.
          </Form.Text>
        </Form.Group>
      </div>

      {loading && <InlineLoadingIcon />}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && !error && (
        <div className="results-section">
          {hasResults ? (
            <>
              <div className="results-header">
                <div className="results-header-title">
                  <span>Search Results ({resultCount})</span>
                  <div className="results-header-actions">
                    <Button
                      className="icon-button"
                      variant="outline-primary"
                      onClick={handleAddSearchToCart}
                    >
                      <FaShoppingCart />
                      <span>Add All Results to Export Cart</span>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="results-list">
                {results.map((investor) => (
                  <InvestorCard
                    key={investor.id}
                    investor={investor}
                    showAdminControls={isUserAdmin && config.showAdminControls}
                    sectorFocusHighlight={sectorFocus ?? undefined}
                    stageFocusHighlight={stageFocus ?? undefined}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="no-results">
              {searchBarValue ? (
                <Alert variant="warning">
                  No investors found matching your search criteria.
                </Alert>
              ) : (
                <Alert variant="info">
                  Enter a search term to find investors.
                </Alert>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestorSearchPage;
